import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import React from 'react'

import { VStack } from '~/components/elements/VStack'
import {
  AnalysisCommentBlock,
  useGenerateSingleComment,
} from '~/components/features/AnalysisCommentBlock'
import { DisplayToggleButton } from '~/components/features/DisplayToggleButton'
import { ShareDropDownMenuButton } from '~/components/features/ShareDropDownMenuButton'
import { SortButton } from '~/components/features/SortButton'
import { Table } from '~/components/features/Table'
import { TableContainer } from '~/components/features/TableContainer'
import { type SegmentMode } from '~/gql/graphql'
import { useKeyedAtomEntry } from '~/hooks/jotai'
import { useSurveyQueryActiveBrandGroup } from '~/hooks/useSurveyQuery'
import { exists, type SegmentDisplayType, serializeTableToText } from '~/utils'

import {
  commentBySegmentDisplayTypeAtom,
  hiddenRowIdsAtom,
  isTabulatedAtom,
  savedCommentBySegmentDisplayTypeAtom,
  segmentDisplayTypeAtom,
  sortValueAtom,
} from '../atoms'
import { useRestoringParams } from '../hooks/restoringParams/useRestoringParams'
import { useDisplayRowItem } from '../hooks/useDisplayRowItem'
import { useExpandText } from '../hooks/useExpandText'
import { useFigureInfo } from '../hooks/useFigureInfo'
import { useSortButtonItems } from '../hooks/useSortButtonItems'
import { useSurveyId } from '../hooks/useSurveyId'
import { useTableColumns } from '../hooks/useTableColumns'
import { useTableInfo } from '../hooks/useTableInfo'
import { useTabulatedColumnCustomerAttributes } from '../hooks/useTabulatedColumnCustomerAttributes'

import { EditColumnCustomerAttributesDiffButton } from './EditColumnCustomerAttributesDiffButton'

// AI コメント生成に渡すセグメント分類は、表に表示中の分類に合わせる。
// both/summary は単一の分類に定まらないため、セグメントラベル表を省く 'none' を渡す。
const segmentModeMap: Record<SegmentDisplayType, SegmentMode> = {
  fiveSegs: 'fiveSegs',
  nineSegs: 'nineSegs',
  both: 'none',
  summary: 'none',
}

export const CustomerAttributeTable = () => {
  const segmentDisplayType = useAtomValue(segmentDisplayTypeAtom)
  const [comment, setComment] = useKeyedAtomEntry(
    commentBySegmentDisplayTypeAtom,
    segmentDisplayType,
  )
  const [savedComment, setSavedComment] = useKeyedAtomEntry(
    savedCommentBySegmentDisplayTypeAtom,
    segmentDisplayType,
  )
  const { generate, isGenerating } = useGenerateSingleComment()
  const surveyId = useSurveyId()
  const brandGroup = useSurveyQueryActiveBrandGroup()
  const sortButtonItems = useSortButtonItems()
  const [sortValue, setSortValue] = useAtom(sortValueAtom)

  const displayRowItems = useDisplayRowItem()
  const setHiddenRowIds = useSetAtom(hiddenRowIdsAtom)

  const columns = useTableColumns()
  const figureInfo = useFigureInfo()
  const restoringParams = useRestoringParams()
  const tableInfo = useTableInfo()
  const { handleTextExpandedChange, isTextExpanded, textLineCount } =
    useExpandText(tableInfo)
  const image = (
    <Table
      columns={columns}
      tableInfo={tableInfo}
      textLineCount={textLineCount}
    />
  )

  const columnCustomerAttributes = useTabulatedColumnCustomerAttributes()

  const segmentMode = segmentModeMap[segmentDisplayType]

  const handleAutoGenerate = React.useCallback(async () => {
    const result = await generate({
      brandName: brandGroup?.name ?? '',
      filterDescription: '',
      filterPatternIndex: 0,
      outputType: 'customerAttributeAnalysis',
      segmentMode,
      surveyId,
      tabulationData: serializeTableToText(columns, tableInfo),
    })
    if (exists(result)) {
      setComment(result)
    }
  }, [
    generate,
    brandGroup?.name,
    segmentMode,
    surveyId,
    columns,
    tableInfo,
    setComment,
  ])

  const isTabulated = useAtomValue(isTabulatedAtom)
  if (!isTabulated) {
    return null
  }

  return (
    <VStack align="start" id="table" spacing={8}>
      <AnalysisCommentBlock
        isGenerating={isGenerating}
        savedComment={savedComment}
        saveDocumentImage={{
          commentReadOnly: true,
          figureInfo,
          image,
          initialComment: comment,
          onSaved: (saved) => setSavedComment(saved),
          restoringParams,
        }}
        value={comment}
        onAutoGenerate={handleAutoGenerate}
        onCommentChange={setComment}
      />
      <TableContainer
        header={
          <>
            <SortButton
              items={sortButtonItems}
              value={sortValue}
              onChange={setSortValue}
            />

            <DisplayToggleButton
              isTextExpanded={isTextExpanded}
              isVisibleExpandTextCheckbox={true}
              rowItems={displayRowItems}
              onHiddenRowChange={setHiddenRowIds}
              onTextExpandedChange={handleTextExpandedChange}
            />

            <ShareDropDownMenuButton
              copyToClipboard={{
                columns,
                figureInfo,
                restoringParams,
                tableInfo,
              }}
              downloadAsPng={{ image }}
              getSharedUrl={{ restoringParams }}
              saveDocumentImage={{
                commentReadOnly: true,
                figureInfo,
                image,
                initialComment: comment,
                onSaved: (saved) => setSavedComment(saved),
                restoringParams,
              }}
            />

            {1 <= columnCustomerAttributes.length && (
              <EditColumnCustomerAttributesDiffButton />
            )}
          </>
        }
      >
        <Table
          columns={columns}
          stickyColumns={2}
          tableInfo={tableInfo}
          textLineCount={textLineCount}
        />
      </TableContainer>
    </VStack>
  )
}
